"use client";

import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import Link from "next/link";
import {
  AlertCircle,
  ArrowRight,
  Check,
  Clock3,
  FileImage,
  FileText,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  SlidersHorizontal,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";
import { cn } from "@/lib/utils";
import {
  TEMPLATE_SCOPE_OPTIONS,
  formatTemplateDate,
  templateBehaviorSummary,
  type SavedTemplate,
  type TemplateScope,
} from "./templateTypes";

type Filter = "all" | "default" | TemplateScope;
type Sort = "recent" | "used" | "name";

function safeSettingsError(error: unknown): string {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  if (message.includes("already exists")) {
    return "A template with this name already exists. Choose another name.";
  }
  if (message.includes("template not found")) {
    return "This template was removed in another session. Refresh your library.";
  }
  return "Darsi could not save these settings. Your changes are still here—try again.";
}

export function TemplateLibrary({
  templates,
  selectedId,
  onSelected,
  onAdd,
  onReplace,
}: {
  templates: SavedTemplate[];
  selectedId: Id<"user_templates"> | null;
  onSelected: (id: Id<"user_templates"> | null) => void;
  onAdd: () => void;
  onReplace: (template: SavedTemplate) => void;
}) {
  const setDefault = useMutation(api.userTemplates.setDefault);
  const updateSettings = useMutation(api.userTemplates.updateSettings);
  const removeTemplate = useMutation(api.userTemplates.remove);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("recent");
  const [settingsId, setSettingsId] = useState<Id<"user_templates"> | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftScope, setDraftScope] = useState<TemplateScope>("default");
  const [busyId, setBusyId] = useState<Id<"user_templates"> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SavedTemplate | null>(null);
  const deleteDialogRef = useRef<HTMLDialogElement>(null);
  const detailsDialogRef = useRef<HTMLDialogElement>(null);

  const availableScopes = useMemo(
    () => TEMPLATE_SCOPE_OPTIONS.filter((option) => templates.some((template) => template.scope === option.value)),
    [templates],
  );
  const visible = useMemo(() => {
    const filtered = templates.filter((template) => {
      if (filter === "all") return true;
      if (filter === "default") return template.isDefault;
      return template.scope === filter;
    });
    return filtered.sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "used") return b.usageCount - a.usageCount || b.updatedAt - a.updatedAt;
      return b.updatedAt - a.updatedAt;
    });
  }, [filter, sort, templates]);
  const selected = templates.find((template) => template._id === selectedId) ?? null;

  useEffect(() => {
    const dialog = detailsDialogRef.current;
    if (!dialog) return;
    if (selected && !dialog.open) {
      dialog.showModal();
      window.requestAnimationFrame(() => {
        dialog.querySelector<HTMLElement>("[data-template-details-primary]")?.focus();
      });
    } else if (!selected && dialog.open) {
      dialog.close();
    }
  }, [selected]);

  const closeDetails = () => {
    detailsDialogRef.current?.close();
  };

  const openSettings = (template: SavedTemplate) => {
    onSelected(template._id);
    setSettingsId(template._id);
    setDraftName(template.name);
    setDraftScope(template.scope);
    setError(null);
  };

  const handleDefault = async (template: SavedTemplate) => {
    setBusyId(template._id);
    setError(null);
    try {
      await setDefault({ templateId: template._id });
      track(ANALYTICS_EVENTS.templateDefaultSet, { scope: template.scope });
    } catch {
      setError("Darsi could not change the default template. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSettingsSave = async (template: SavedTemplate) => {
    setBusyId(template._id);
    setError(null);
    try {
      await updateSettings({ templateId: template._id, name: draftName, scope: draftScope });
      if (draftName.trim() !== template.name) track(ANALYTICS_EVENTS.templateRenamed);
      if (draftScope !== template.scope) track(ANALYTICS_EVENTS.templateScopeUpdated, { scope: draftScope });
      setSettingsId(null);
    } catch (caught) {
      setError(safeSettingsError(caught));
    } finally {
      setBusyId(null);
    }
  };

  const openDelete = (template: SavedTemplate) => {
    setDeleteTarget(template);
    deleteDialogRef.current?.showModal();
  };

  const handleDelete = async () => {
    if (!deleteTarget || busyId) return;
    setBusyId(deleteTarget._id);
    setError(null);
    try {
      await removeTemplate({ templateId: deleteTarget._id });
      track(ANALYTICS_EVENTS.templateDeleted, {
        was_default: deleteTarget.isDefault,
        usage_count: deleteTarget.usageCount,
      });
      if (selectedId === deleteTarget._id) onSelected(null);
      deleteDialogRef.current?.close();
      setDeleteTarget(null);
    } catch {
      setError("Darsi could not delete this template. It may have already been removed in another session.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-w-0">
      <section aria-labelledby="saved-templates-heading" className="min-w-0">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-4">
          <div>
            <h2 id="saved-templates-heading" className="text-lg font-bold tracking-tight">Saved templates</h2>
            <p className="mt-1 text-sm text-muted-foreground">{templates.length} {templates.length === 1 ? "structure" : "structures"} in your private library</p>
          </div>
          <button type="button" onClick={onAdd} className="template-primary-button">
            <Plus className="h-4 w-4" />Add template
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2" aria-label="Filter templates">
            {[{ value: "all", label: "All" }, { value: "default", label: "Default" }, ...availableScopes].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFilter(option.value as Filter)}
                aria-pressed={filter === option.value}
                className={cn(
                  "min-h-9 whitespace-nowrap rounded-lg border px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  filter === option.value ? "border-success bg-muted text-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground",
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
            <SlidersHorizontal className="h-4 w-4" />
            <span className="sr-only">Sort templates</span>
            <select value={sort} onChange={(event) => setSort(event.target.value as Sort)} className="template-input min-w-40">
              <option value="recent">Recently updated</option>
              <option value="used">Most used</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>

        {error ? (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-destructive/35 bg-destructive/5 p-4 text-sm" role="alert">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p>{error}</p>
          </div>
        ) : null}

        {visible.length ? (
          <ul className="mt-4 grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visible.map((template) => (
              <li key={template._id} className={cn("min-w-0 rounded-xl border bg-card p-4", selectedId === template._id ? "border-success" : "border-border")}>
                <article className="flex h-full min-w-0 flex-col">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-success">
                      <FileText className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="min-w-0 truncate font-bold">{template.name}</h3>
                        {template.isDefault ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs font-bold text-foreground"><Star className="h-3 w-3" fill="currentColor" />Default template</span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs font-semibold text-muted-foreground">{template.scopeLabel} · {template.styleProfile.stageSequence.length} detected stages</p>
                    </div>
                    <details className="relative shrink-0">
                      <summary className="template-icon-button cursor-pointer list-none" aria-label={`Actions for ${template.name}`}><MoreHorizontal className="h-5 w-5" /></summary>
                      <div className="absolute right-0 z-20 mt-1 w-52 rounded-xl border border-border bg-popover p-1 shadow-lg">
                        <button type="button" onClick={() => openSettings(template)} className="flex min-h-10 w-full items-center gap-2 rounded-lg px-3 text-left text-sm font-semibold hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Pencil className="h-4 w-4" />Rename or edit scope</button>
                        {!template.isDefault ? <button type="button" disabled={busyId === template._id} onClick={() => void handleDefault(template)} className="flex min-h-10 w-full items-center gap-2 rounded-lg px-3 text-left text-sm font-semibold hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"><Star className="h-4 w-4" />{busyId === template._id ? "Updating…" : "Make default"}</button> : null}
                        <button type="button" onClick={() => { track(ANALYTICS_EVENTS.templateSourceReplaceStarted); onReplace(template); }} className="flex min-h-10 w-full items-center gap-2 rounded-lg px-3 text-left text-sm font-semibold hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><RefreshCw className="h-4 w-4" />Replace and reanalyze</button>
                        <button type="button" onClick={() => openDelete(template)} className="flex min-h-10 w-full items-center gap-2 rounded-lg px-3 text-left text-sm font-semibold text-destructive hover:bg-destructive/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Trash2 className="h-4 w-4" />Delete</button>
                      </div>
                    </details>
                  </div>
                  <p className="mt-4 line-clamp-2 text-sm leading-6 text-muted-foreground">{templateBehaviorSummary(template)}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>Updated {formatTemplateDate(template.updatedAt)}</span>
                    <span>{template.usageCount ? `Used ${template.usageCount} ${template.usageCount === 1 ? "time" : "times"}` : "Not used yet"}</span>
                  </div>
                  <div className="mt-auto flex flex-wrap gap-2 pt-5">
                    <Link href={`/dashboard?template=${template._id}`} className="template-primary-button flex-1">
                      Create lesson <ArrowRight className="h-4 w-4" />
                    </Link>
                    <button type="button" onClick={() => { onSelected(template._id); track(ANALYTICS_EVENTS.templateViewed, { scope: template.scope, is_default: template.isDefault, usage_count: template.usageCount }); }} className="template-secondary-button">
                      View details
                    </button>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-5 rounded-xl border border-dashed border-border p-8 text-center">
            <p className="font-bold">No templates match this filter</p>
            <button type="button" onClick={() => setFilter("all")} className="mt-2 text-sm font-semibold text-success underline underline-offset-4">Show all templates</button>
          </div>
        )}
      </section>

      <dialog
        ref={detailsDialogRef}
        aria-labelledby="template-details-title"
        className="template-dialog template-details-dialog m-0 h-dvh max-h-dvh w-full max-w-none border-0 bg-card p-0 text-foreground backdrop:bg-[var(--modal-backdrop)] sm:m-auto sm:h-auto sm:max-h-[min(88dvh,900px)] sm:w-[min(760px,calc(100%-2rem))] sm:rounded-2xl sm:border sm:border-border sm:shadow-xl"
        onCancel={(event) => {
          if (busyId) event.preventDefault();
        }}
        onClose={() => {
          if (selectedId) onSelected(null);
          setSettingsId(null);
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget && !busyId) closeDetails();
        }}
      >
        {selected ? (
          <TemplateDetails
            template={selected}
            editing={settingsId === selected._id}
            draftName={draftName}
            draftScope={draftScope}
            busy={busyId === selected._id}
            onClose={closeDetails}
            onEdit={() => openSettings(selected)}
            onNameChange={setDraftName}
            onScopeChange={setDraftScope}
            onSave={() => void handleSettingsSave(selected)}
            onCancelEdit={() => setSettingsId(null)}
            onDefault={() => void handleDefault(selected)}
            onReplace={() => { closeDetails(); track(ANALYTICS_EVENTS.templateSourceReplaceStarted); onReplace(selected); }}
            onDelete={() => openDelete(selected)}
          />
        ) : null}
      </dialog>

      <dialog ref={deleteDialogRef} aria-labelledby="delete-template-title" className="template-dialog m-auto w-[min(480px,calc(100%-1.5rem))] rounded-2xl border border-border bg-card p-0 text-foreground shadow-xl backdrop:bg-[var(--modal-backdrop)]">
        {deleteTarget ? (
          <div>
            <header className="flex items-start justify-between gap-3 border-b border-border p-5">
              <div><p className="text-sm font-semibold text-destructive">Delete template</p><h2 id="delete-template-title" className="mt-1 text-lg font-bold">Delete “{deleteTarget.name}”?</h2></div>
              <button type="button" className="template-icon-button" aria-label="Close delete confirmation" onClick={() => deleteDialogRef.current?.close()}><X className="h-5 w-5" /></button>
            </header>
            <div className="p-5 text-sm leading-6 text-muted-foreground">
              <p>The stored source pages and extracted template data will be deleted. Lesson plans already created with this template remain in My resources.</p>
              {deleteTarget.isDefault ? <p className="mt-3 font-semibold text-foreground">No replacement default is chosen automatically.</p> : null}
            </div>
            <footer className="flex justify-end gap-2 border-t border-border p-4">
              <button type="button" className="template-secondary-button" onClick={() => deleteDialogRef.current?.close()}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget._id} className="inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-lg bg-destructive px-4 text-sm font-bold text-destructive-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50" onClick={() => void handleDelete()}><Trash2 className="h-4 w-4" />{busyId === deleteTarget._id ? "Deleting…" : "Delete template"}</button>
            </footer>
          </div>
        ) : null}
      </dialog>
    </div>
  );
}

function TemplateDetails({
  template,
  editing,
  draftName,
  draftScope,
  busy,
  onClose,
  onEdit,
  onNameChange,
  onScopeChange,
  onSave,
  onCancelEdit,
  onDefault,
  onReplace,
  onDelete,
}: {
  template: SavedTemplate;
  editing: boolean;
  draftName: string;
  draftScope: TemplateScope;
  busy: boolean;
  onClose: () => void;
  onEdit: () => void;
  onNameChange: (value: string) => void;
  onScopeChange: (value: TemplateScope) => void;
  onSave: () => void;
  onCancelEdit: () => void;
  onDefault: () => void;
  onReplace: () => void;
  onDelete: () => void;
}) {
  const profile = template.styleProfile;
  return (
    <div className="flex h-dvh min-h-0 flex-col bg-card sm:h-auto sm:max-h-[min(88dvh,900px)]">
      <header className="template-details-header flex shrink-0 items-start gap-3 border-b border-border p-5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">{template.isDefault ? <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs font-bold"><Star className="h-3 w-3" fill="currentColor" />Default template</span> : null}<span className="text-xs font-semibold text-muted-foreground">{template.scopeLabel}</span></div>
          <h2 id="template-details-title" className="mt-2 break-words text-xl font-bold tracking-tight">{template.name}</h2>
        </div>
        <button type="button" onClick={onClose} disabled={busy} aria-label="Close template details" className="template-icon-button"><X className="h-5 w-5" /></button>
      </header>

      <div className="template-details-scroll min-h-0 flex-1 space-y-6 overflow-y-auto p-5">
        <Link data-template-details-primary href={`/dashboard?template=${template._id}`} className="template-primary-button w-full">Create lesson with template <ArrowRight className="h-4 w-4" /></Link>

        {editing ? (
          <section aria-labelledby="template-settings-heading" className="rounded-xl border border-border p-4">
            <h3 id="template-settings-heading" className="font-bold">Template settings</h3>
            <label className="mt-3 block text-sm font-semibold">Name<input value={draftName} onChange={(event) => onNameChange(event.target.value)} className="template-input mt-1.5" /></label>
            <label className="mt-3 block text-sm font-semibold">Applies to<select value={draftScope} onChange={(event) => onScopeChange(event.target.value as TemplateScope)} className="template-input mt-1.5">{TEMPLATE_SCOPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <div className="mt-4 flex gap-2"><button type="button" disabled={busy || !draftName.trim()} onClick={onSave} className="template-primary-button"><Check className="h-4 w-4" />Save settings</button><button type="button" disabled={busy} onClick={onCancelEdit} className="template-secondary-button">Cancel</button></div>
          </section>
        ) : null}

        <section>
          <div className="flex items-center justify-between gap-3"><h3 className="font-bold">Lesson sequence</h3><span className="text-xs text-muted-foreground">{profile.stageSequence.length} stages</span></div>
          <ol className="mt-3 space-y-3">
            {profile.stageSequence.map((stage, index) => (
              <li key={`${index}-${stage.stage}`} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-sm font-bold">{index + 1}</span>
                <div className="min-w-0 border-b border-border pb-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2"><h4 className="font-bold">{stage.stage}</h4>{stage.typicalTimingMinutes ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock3 className="h-3 w-3" />{stage.typicalTimingMinutes} min</span> : null}</div>
                  {stage.purpose ? <p className="mt-1 text-sm leading-6 text-muted-foreground">{stage.purpose}</p> : null}
                  {stage.typicalInteraction ? <p className="mt-1 text-xs font-semibold text-muted-foreground">{stage.typicalInteraction}</p> : null}
                  {stage.teacherAction ? <p className="mt-2 text-xs leading-5"><strong>Teacher:</strong> <span className="text-muted-foreground">{stage.teacherAction}</span></p> : null}
                  {stage.studentAction ? <p className="mt-1 text-xs leading-5"><strong>Learners:</strong> <span className="text-muted-foreground">{stage.studentAction}</span></p> : null}
                  {stage.assessmentStrategy ? <p className="mt-1 text-xs leading-5"><strong>Checking:</strong> <span className="text-muted-foreground">{stage.assessmentStrategy}</span></p> : null}
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section>
          <h3 className="font-bold">What Darsi follows</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{profile.writingStyleSummary}</p>
          <dl className="mt-4 space-y-3 text-sm">
            {template.detectedLessonType ? <div><dt className="text-xs font-bold text-muted-foreground">Detected lesson type</dt><dd className="mt-1">{TEMPLATE_SCOPE_OPTIONS.find((option) => option.value === template.detectedLessonType)?.label}</dd></div> : null}
            <div><dt className="text-xs font-bold text-muted-foreground">Writing style</dt><dd className="mt-1">{profile.tone} · {profile.procedureDetailLevel} procedures</dd></div>
            {profile.teachingApproach ? <div><dt className="text-xs font-bold text-muted-foreground">Teaching approach</dt><dd className="mt-1">{profile.teachingApproach}</dd></div> : null}
            {profile.interactionPatterns.length ? <div><dt className="text-xs font-bold text-muted-foreground">Interaction</dt><dd className="mt-1">{profile.interactionPatterns.join(" · ")}</dd></div> : null}
            {profile.differentiationApproach ? <div><dt className="text-xs font-bold text-muted-foreground">Differentiation</dt><dd className="mt-1">{profile.differentiationApproach}</dd></div> : null}
            {profile.assessmentApproach ? <div><dt className="text-xs font-bold text-muted-foreground">Assessment</dt><dd className="mt-1">{profile.assessmentApproach}</dd></div> : null}
          </dl>
        </section>

        <section>
          <h3 className="font-bold">Source</h3>
          <p className="mt-1 text-sm text-muted-foreground">{template.sourceKind === "docx" ? "Word document structure" : `${template.pagesAnalyzed} analyzed ${template.pagesAnalyzed === 1 ? "page" : "pages"}`}</p>
          {template.sourceMissing ? <p className="mt-2 flex items-start gap-2 text-sm text-warning"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />One or more source pages are no longer available.</p> : null}
          {template.sourcePreviewUrls.length ? (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {template.sourcePreviewUrls.map((url, index) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="overflow-hidden rounded-lg border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={url} alt={`Source page ${index + 1}`} className="aspect-[4/5] w-full object-cover object-top" />
                  <span className="block border-t border-border px-2 py-1.5 text-xs font-semibold">Page {index + 1}</span>
                </a>
              ))}
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-muted/45 p-3 text-xs text-muted-foreground">
              {template.sourceKind === "docx" ? <FileText className="h-4 w-4" /> : <FileImage className="h-4 w-4" />}No retained page preview
            </div>
          )}
        </section>

        <dl className="grid grid-cols-2 gap-4 border-t border-border pt-4 text-sm">
          <div><dt className="text-xs font-bold text-muted-foreground">Created</dt><dd className="mt-1">{formatTemplateDate(template.createdAt)}</dd></div>
          <div><dt className="text-xs font-bold text-muted-foreground">Updated</dt><dd className="mt-1">{formatTemplateDate(template.updatedAt)}</dd></div>
          <div><dt className="text-xs font-bold text-muted-foreground">Usage</dt><dd className="mt-1">{template.usageCount ? `${template.usageCount} accepted ${template.usageCount === 1 ? "lesson" : "lessons"}` : "Not used yet"}</dd></div>
          <div><dt className="text-xs font-bold text-muted-foreground">Last used</dt><dd className="mt-1">{template.lastUsedAt ? formatTemplateDate(template.lastUsedAt) : "—"}</dd></div>
        </dl>

        <div className="flex flex-wrap gap-2 border-t border-border pt-4">
          <button type="button" onClick={onEdit} className="template-secondary-button"><Pencil className="h-4 w-4" />Edit settings</button>
          {!template.isDefault ? <button type="button" disabled={busy} onClick={onDefault} className="template-secondary-button"><Star className="h-4 w-4" />Make default</button> : null}
          <button type="button" onClick={onReplace} className="template-secondary-button"><RefreshCw className="h-4 w-4" />Replace source</button>
          <button type="button" onClick={onDelete} className="template-secondary-button text-destructive"><Trash2 className="h-4 w-4" />Delete</button>
        </div>
      </div>
    </div>
  );
}
